import { Box, Chip, Collapse, IconButton, Stack, Typography } from '@mui/material'
import JsonView from '@uiw/react-json-view'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { C1Icon } from 'components/core/icons/C1Icon'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { UserValue } from 'lib/actions/user'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { StepUpTransaction, StepUpTransactionState } from 'pbts/c1/api/stepup/v1/stepup'
import { UserServiceGetRequest } from 'pbts/c1/api/user/v1/user'
import { Struct } from 'pbts/google/protobuf/struct'
import { FC, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { getUserApi } from 'redux_toolkit/users'
import { StepUpProviderFetcher } from './StepUpProviderFetcher'

/**
 * Displays the state of a step-up transaction with appropriate styling
 */
const StepUpTransactionStateChip: FC<{ state: StepUpTransactionState }> = ({ state }) => {
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default'
    let label = <FormattedMessage id="component_step_up_transaction_state_unknown" defaultMessage="Unknown" />

    switch (state) {
        case StepUpTransactionState.PENDING:
            color = 'warning'
            label = <FormattedMessage id="component_step_up_transaction_state_pending" defaultMessage="Pending" />
            break
        case StepUpTransactionState.VERIFIED:
            color = 'success'
            label = <FormattedMessage id="component_step_up_transaction_state_verified" defaultMessage="Verified" />
            break
        case StepUpTransactionState.ERROR:
            color = 'error'
            label = <FormattedMessage id="component_step_up_transaction_state_error" defaultMessage="Error" />
            break
        default:
            color = 'default'
            label = <FormattedMessage id="component_step_up_transaction_state_unknown" defaultMessage="Unknown" />
            break
    }

    return <Chip size="small" color={color} label={label} />
}

/**
 * Props for the StepUpTransactionDisplay component
 */
export interface StepUpTransactionDisplayProps {
    /**
     * The step-up transaction to display
     */
    transaction: StepUpTransaction
    /**
     * Whether to show the transaction ID
     */
    showId?: boolean
    /**
     * Whether to show the user who performed the step-up
     */
    showUser?: boolean
    /**
     * Whether to show the claims
     */
    showClaims?: boolean
}

/**
 * Displays information about a step-up transaction
 */
export const StepUpTransactionDisplay: FC<StepUpTransactionDisplayProps> = ({
    transaction,
    showId = true,
    showUser = true,
    showClaims = true,
}) => {
    const [claimsExpanded, setClaimsExpanded] = useState<boolean>(false)
    const { data: user } = getUserApi.useGet(
        UserServiceGetRequest.create({
            id: transaction.userId,
        }),
    )

    const toggleClaims = (): void => {
        setClaimsExpanded(!claimsExpanded)
    }

    const hasClaims = transaction.claims && Object.keys(transaction.claims.fields || {}).length > 0
    const hasError = transaction.state === StepUpTransactionState.ERROR && transaction.errorMessage

    return (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={1}>
                {/* Header row with state and ID */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <StepUpTransactionStateChip state={transaction.state} />

                    {showId && (
                        <C1CopyText
                            value={transaction.id}
                            typographyVariant="body1"
                            truncateWidth="200px"
                        />
                    )}
                </Stack>

                {/* Provider */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography color="text.secondary" sx={{ mr: 1 }}>
                        <FormattedMessage
                            id="component_step_up_transaction_provider"
                            defaultMessage="Provider:"
                        />
                    </Typography>
                    <StepUpProviderFetcher providerId={transaction.providerId} />
                </Box>

                {/* Error message if present */}
                {hasError && (
                    <Typography color="error">
                        <FormattedMessage
                            id="component_step_up_transaction_error"
                            defaultMessage="Error: {message}"
                            values={{
                                message: transaction.errorMessage,
                            }}
                        />
                    </Typography>
                )}

                {/* User who performed the step-up */}
                {showUser && user && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography color="text.secondary" sx={{ mr: 1 }}>
                            <FormattedMessage
                                id="component_step_up_transaction_user"
                                defaultMessage="Authenticated by:"
                            />
                        </Typography>
                        <UserDisplayName user={user as UserValue} includeDetails={false} showTooltip />
                    </Box>
                )}

                {/* Claims section with toggle */}
                {showClaims && hasClaims && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography color="text.secondary">
                                <FormattedMessage
                                    id="component_step_up_transaction_claims"
                                    defaultMessage="Claims"
                                />
                            </Typography>
                            <IconButton size="small" onClick={toggleClaims} aria-label="toggle claims">
                                {claimsExpanded ? <C1Icon icon={ChevronUp} size="sm" /> : <C1Icon icon={ChevronDown} size="sm" />}
                            </IconButton>
                        </Box>
                        <Collapse in={claimsExpanded}>
                            <Box sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1, maxHeight: '200px', overflow: 'auto' }}>
                                {transaction.claims && (
                                    <JsonView value={Struct.toJson(transaction.claims) as object} />
                                )}
                            </Box>
                        </Collapse>
                    </Box>
                )}
            </Stack>
        </Box>
    )
}
